/**
 * The single active-call state machine for the web client. One call at a time.
 *
 * States: idle, ringing-out, ringing-in, connecting, active, ended. This module
 * is the sole owner of transitions, timers, and the media client. UI surfaces
 * (incoming-call modal, in-call overlay, thread call buttons) subscribe to it
 * and call its methods. Socket events are translated into method calls by
 * index.js.
 *
 * Timing constants are one authoritative set shared with the backend contract:
 * a 40s ring and a 15s media-connection watchdog (README "Ring timing" and
 * "Media-connection failure").
 */

import { callApi, CallApiError } from './call-api.js';
import { ringTone } from './ring-tone.js';

const RING_DURATION_MS = 40000;
const MEDIA_CONNECT_TIMEOUT_MS = 15000;
// A room reconnect and a duplicate identity both drop the peer for a while, so
// a peer disconnect ends the call only after this window in a connected room.
// Room Disconnected and the CALL_END socket event carry a real end.
export const PEER_DISCONNECT_GRACE_MS = 10000;

export const CallStatus = {
    IDLE: 'idle',
    RINGING_OUT: 'ringing-out',
    RINGING_IN: 'ringing-in',
    CONNECTING: 'connecting',
    ACTIVE: 'active',
    ENDED: 'ended',
};

export const EMPTY_AVATAR = '//ipstatic.net/images/male-empty-profile.png';

/**
 * True when an 'ended' summary gets the after-call screen instead of a toast:
 * the call connected in this tab, whoever ended it.
 */
export function hasAfterCallScreen(summary) {
    return !!summary
        && summary.connected === true
        && summary.reason !== 'connect-failed';
}

export function avatarUrl(user) {
    if (!user) {
        return EMPTY_AVATAR;
    }
    if (typeof user.avatar === 'string') {
        return user.avatar || EMPTY_AVATAR;
    }
    const avatar = user.avatar || {};
    // Use a bounded thumbnail served at an allowed resolution. The static host
    // only serves a fixed set of thumbnail sizes, and 300px (thumb_xlarge)
    // covers the largest overlay avatar (112px) at 2x. avatar.url is the
    // full-size original (a non-thumbnail, multi-megabyte source) and is only a
    // last resort before the empty placeholder.
    return avatar.thumb_xlarge
        || avatar.thumb_large
        || avatar.thumb_medium
        || avatar.thumb_small
        || avatar.url
        || EMPTY_AVATAR;
}

function normalizeUser(user) {
    if (!user) {
        return null;
    }
    return {
        id: String(user.id || ''),
        username: user.username || '',
        name: user.name || user.username || '',
        avatar: avatarUrl(user),
    };
}

class CallState {
    constructor() {
        this.status = CallStatus.IDLE;
        this.call = null;
        this.callId = null;
        this.role = null;
        this.type = null;
        this.other = null;
        this.livekit = null;
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.endReason = null;
        this.roomConnected = true;
        this.peerMissing = false;
        this.peerCameraOff = false;
        this.peerMicMuted = false;
        this.weakConnection = false;
        this.facing = 'user';

        this.client = null;
        this._placing = false;
        this._finishing = false;
        this._seenVersion = new Map();
        this._mediaReady = false;
        this._ringTimer = null;
        this._connectTimer = null;
        this._peerGraceTimer = null;
        this._changeListeners = new Set();
        this._eventListeners = new Map();
    }

    // --- subscription -----------------------------------------------------

    subscribe(cb) {
        this._changeListeners.add(cb);
        return () => this._changeListeners.delete(cb);
    }

    on(event, cb) {
        if (!this._eventListeners.has(event)) {
            this._eventListeners.set(event, new Set());
        }
        this._eventListeners.get(event).add(cb);
        return () => this._eventListeners.get(event)?.delete(cb);
    }

    _emitChange() {
        const snap = this.snapshot();
        this._changeListeners.forEach((cb) => {
            try {
                cb(snap);
            } catch (e) {
                /* a listener error must not break the state machine */
            }
        });
    }

    _emit(event, detail) {
        this._eventListeners.get(event)?.forEach((cb) => {
            try {
                cb(detail);
            } catch (e) {
                /* isolate listener errors */
            }
        });
    }

    snapshot() {
        return {
            status: this.status,
            callId: this.callId,
            role: this.role,
            type: this.type,
            other: this.other,
            muted: this.muted,
            cameraOff: this.cameraOff,
            startedAt: this.startedAt,
            endReason: this.endReason,
            reconnecting: this.status === CallStatus.ACTIVE && (!this.roomConnected || this.peerMissing),
            peerCameraOff: this.peerCameraOff,
            peerMicMuted: this.peerMicMuted,
            weakConnection: this.weakConnection,
            facing: this.facing,
        };
    }

    isBusy() {
        return this.status !== CallStatus.IDLE && this.status !== CallStatus.ENDED;
    }

    // --- dedup / ordering -------------------------------------------------

    /**
     * Drop a socket event whose state_version is not newer than the latest
     * seen for its call. Events with no version (authorization events) are
     * handled elsewhere and never reach here.
     */
    _isStale(callId, version) {
        if (callId === null || callId === undefined || version === undefined || version === null) {
            return false;
        }
        const key = String(callId);
        const seen = this._seenVersion.get(key);
        const next = Number(version);
        if (seen !== undefined && next <= seen) {
            return true;
        }
        this._seenVersion.set(key, next);
        return false;
    }

    // --- outgoing (caller) ------------------------------------------------

    async startOutgoing(targetId, type, other) {
        if (this.isBusy() || this._placing) {
            return;
        }

        // Place the call before showing any UI, so a rejected create (busy,
        // rate limit, gating) surfaces as an error toast with no overlay flash.
        this._placing = true;
        let result;
        try {
            result = await callApi.createCall(targetId, type);
        } finally {
            this._placing = false;
        }

        this.status = CallStatus.RINGING_OUT;
        this.role = 'caller';
        this.type = type;
        this.other = normalizeUser(other) || { id: String(targetId), username: '', name: '', avatar: EMPTY_AVATAR };
        this.call = result;
        this.callId = String(result.id);
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.endReason = null;
        this._isStale(this.callId, result.state_version);

        // A 201 create carries fresh join credentials. A 200 idempotent replay of
        // an existing pending call does not, so fetch a token to (re)join.
        let credentials = result.livekit || null;
        if (!credentials) {
            try {
                const token = await callApi.getToken(this.callId);
                credentials = token.livekit || null;
            } catch (e) {
                credentials = null;
            }
        }

        // CONNECTING is as valid as RINGING_OUT here: a reconnect reconcile
        // that observed the callee's accept during the token await promotes to
        // CONNECTING, and bailing on it would strand the answered call with no
        // media join (the watchdog would then end it as connect_failed).
        if (
            (this.status !== CallStatus.RINGING_OUT && this.status !== CallStatus.CONNECTING)
            || this.callId !== String(result.id)
        ) {
            // The call was already resolved (declined/canceled) while we awaited.
            return;
        }

        this.livekit = credentials;
        if (this.status === CallStatus.RINGING_OUT) {
            this._startRingTimer(() => this._onOutgoingTimeout());
            ringTone.startOutgoing();
        }
        this._emitChange();

        this._joinRoom({ isRingback: true });
    }

    _onOutgoingTimeout() {
        if (this.status !== CallStatus.RINGING_OUT) {
            return;
        }
        if (this.callId) {
            callApi.cancelCall(this.callId, 'unanswered').catch(() => {});
        }
        this._finish('unanswered', { toast: 'no_answer' });
    }

    // --- incoming (callee) ------------------------------------------------

    incomingRing(call, caller, livekitUrl) {
        if (!call || !call.id) {
            return;
        }
        if (this._isStale(call.id, call.state_version)) {
            return;
        }
        // Already busy with another call: ignore the ring. The backend busy check
        // should prevent this, but a late socket must never hijack a live call.
        if (this.isBusy() && String(call.id) !== this.callId) {
            return;
        }

        this.status = CallStatus.RINGING_IN;
        this.role = 'callee';
        this.type = call.type;
        this.call = call;
        this.callId = String(call.id);
        this.other = normalizeUser(caller);
        this.livekit = { url: livekitUrl || '', token: null };
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.endReason = null;

        this._startRingTimer(() => this._finish('unanswered', { toast: 'missed' }));
        ringTone.startIncoming();
        this._emitChange();
    }

    async accept() {
        if (this.status !== CallStatus.RINGING_IN || !this.callId) {
            return;
        }
        const expectedCallId = this.callId;
        this._clearRingTimer();
        ringTone.stop();
        this.status = CallStatus.CONNECTING;
        this._emitChange();

        let result;
        try {
            result = await callApi.acceptCall(this.callId);
        } catch (error) {
            if (this.status === CallStatus.CONNECTING && this.callId === expectedCallId) {
                this._finish('error', { toast: this._errorToast(error) });
            }
            return;
        }

        if (this.status !== CallStatus.CONNECTING || this.callId !== expectedCallId) {
            // The user hung up (or the call was reconciled away) while the
            // accept was in flight. The accept won regardless: the backend row
            // is now 'started', and the /end a hang-up sent before this
            // response lost its CAS against the then-still-pending row. End it
            // now, or both users stay "busy" until the zombie sweep.
            callApi.endCall(expectedCallId, 'connect_failed').catch(() => {});
            return;
        }

        this.call = result;
        this.livekit = result.livekit || this.livekit;
        this._startConnectWatchdog();
        this._emitChange();
        this._joinRoom({ isRingback: false });
    }

    decline() {
        if (this.status !== CallStatus.RINGING_IN || !this.callId) {
            return;
        }
        const id = this.callId;
        callApi.declineCall(id).catch(() => {});
        this._finish('declined-local', null);
    }

    // --- media join -------------------------------------------------------

    async _joinRoom({ isRingback }) {
        const expectedCallId = this.callId;
        const credentials = this.livekit;
        if (!credentials || !credentials.url || !credentials.token) {
            if (isRingback) {
                // Caller could not obtain a token: give up cleanly.
                if (this.callId) {
                    callApi.cancelCall(this.callId).catch(() => {});
                }
                this._finish('error', { toast: 'unavailable' });
            } else {
                this._connectFailed();
            }
            return;
        }

        const wantVideo = this.type === 'video';
        this._mediaReady = false;
        try {
            const { CallClient } = await import(/* webpackChunkName: "livekit" */ './call-client.js');
            if (!this._canUseJoin(expectedCallId, isRingback)) {
                return;
            }
            const client = new CallClient({
                onRemoteTrack: (track) => this._ifCurrent(expectedCallId, () => this._emit('remote-track', { track })),
                onLocalVideoTrack: (track) => this._ifCurrent(expectedCallId, () => this._emit('local-video-track', { track })),
                onTrackRemoved: (track) => this._ifCurrent(expectedCallId, () => this._emit('track-removed', { track })),
                onPeerConnected: () => this._ifCurrent(expectedCallId, () => this._onPeerConnected()),
                onPeerDisconnected: () => this._ifCurrent(expectedCallId, () => this._onPeerDisconnected()),
                onDisconnected: () => this._ifCurrent(expectedCallId, () => this._onRoomDisconnected()),
                onConnectionChange: (connected) => this._ifCurrent(expectedCallId, () => this._onConnectionChange(connected)),
                onCameraChange: (cameras) => this._ifCurrent(expectedCallId, () => this._onCameraChange(cameras)),
                onPeerMicChange: (muted) => this._ifCurrent(expectedCallId, () => this._setFlag('peerMicMuted', muted)),
                onQualityChange: (weak) => this._ifCurrent(expectedCallId, () => this._setFlag('weakConnection', weak)),
            });
            this.client = client;
            await client.connect(credentials.url, credentials.token, { video: wantVideo });
            if (!this._canUseJoin(expectedCallId, isRingback) || this.client !== client) {
                try {
                    client.disconnect();
                } catch (e) {
                    /* ignore */
                }
                if (this.client === client) {
                    this.client = null;
                }
                return;
            }
            this.cameraOff = wantVideo ? !client.isCameraOn() : true;
            this._mediaReady = true;

            // The other party may already be in the room (the caller always joins
            // first), so reconcile immediately after connect. This also completes
            // an activation a peer-connected event deferred while the local
            // permission prompt was still open.
            if (client.hasRemoteParticipant()) {
                this._onPeerConnected();
            }
            this._emitChange();
        } catch (error) {
            if (!this._canUseJoin(expectedCallId, isRingback)) {
                return;
            }
            if (isRingback && this.status === CallStatus.RINGING_OUT) {
                // Caller's own ringback never connected: cancel, do not attribute
                // connect_failed (that is a post-accept reason only).
                if (this.callId) {
                    callApi.cancelCall(this.callId).catch(() => {});
                }
                this._finish('error', { toast: 'unavailable' });
            } else {
                // A rejected connect() means local media never came up (a denied
                // camera/microphone prompt, a device failure). Terminal even if
                // a peer join already promoted the status to active while the
                // permission prompt was open — _connectFailed()'s ACTIVE guard
                // would swallow that and leave a live call with no local media.
                if (this.callId) {
                    callApi.endCall(this.callId, 'connect_failed').catch(() => {});
                }
                this._finish('connect-failed', { toast: 'connect_failed' });
            }
        }
    }

    _canUseJoin(expectedCallId, isRingback) {
        if (!expectedCallId || this.callId !== expectedCallId) {
            return false;
        }

        if (isRingback) {
            return this.status === CallStatus.RINGING_OUT
                || this.status === CallStatus.CONNECTING
                || this.status === CallStatus.ACTIVE;
        }

        return this.status === CallStatus.CONNECTING || this.status === CallStatus.ACTIVE;
    }

    _ifCurrent(expectedCallId, cb) {
        if (this.callId === expectedCallId) {
            cb();
        }
    }

    _onPeerConnected() {
        this._clearPeerGraceTimer();
        if (this.peerMissing) {
            this.peerMissing = false;
            this._emitChange();
        }
        // Activation requires local media too: the peer being present says
        // nothing about our own mic/camera, whose permission prompt may still
        // be open (connect() unresolved). Promoting here would clear the
        // watchdog and leave an "active" call with no local audio for as long
        // as the prompt sits unanswered. _joinRoom re-runs this once connect()
        // resolves, so activation happens on whichever of peer-presence /
        // media-ready completes last; the watchdog stays armed until then.
        if (!this._mediaReady) {
            return;
        }
        this._clearConnectWatchdog();
        this._clearRingTimer();
        if (this.status === CallStatus.RINGING_OUT || this.status === CallStatus.CONNECTING) {
            this.status = CallStatus.ACTIVE;
            this.startedAt = Date.now();
            ringTone.stop();
            this._emitChange();
        }
    }

    _onPeerDisconnected() {
        if (this.status !== CallStatus.ACTIVE && this.status !== CallStatus.CONNECTING) {
            return;
        }
        if (this.client && this.client.hasRemoteParticipant()) {
            return;
        }
        this.peerMissing = true;
        this._emitChange();
        this._startPeerGraceTimer();
    }

    // A room reconnect removes every remote participant before it reports
    // Reconnecting, so only a connected room can prove that the peer left.
    _onConnectionChange(connected) {
        this.roomConnected = connected;
        if (!connected) {
            this._clearPeerGraceTimer();
        } else if (this.peerMissing && !(this.client && this.client.hasRemoteParticipant())) {
            this._startPeerGraceTimer();
        }
        this._emitChange();
    }

    _onCameraChange({ local, remote }) {
        const cameraOff = this.type === 'video' ? local : true;
        if (cameraOff === this.cameraOff && remote === this.peerCameraOff) {
            return;
        }
        this.cameraOff = cameraOff;
        this.peerCameraOff = remote;
        this._emitChange();
    }

    _setFlag(name, value) {
        if (this[name] === value) {
            return;
        }
        this[name] = value;
        this._emitChange();
    }

    _startPeerGraceTimer() {
        if (this._peerGraceTimer) {
            return;
        }
        const expectedCallId = this.callId;
        this._peerGraceTimer = setTimeout(() => {
            this._peerGraceTimer = null;
            if (!expectedCallId || this.callId !== expectedCallId) {
                return;
            }
            if (this.status !== CallStatus.ACTIVE && this.status !== CallStatus.CONNECTING) {
                return;
            }
            if (!this.client || !this.client.isConnected() || this.client.hasRemoteParticipant()) {
                return;
            }
            callApi.endCall(expectedCallId, this._endReason()).catch(() => {});
            this._finish('remote-ended', { toast: 'ended' });
        }, PEER_DISCONNECT_GRACE_MS);
    }

    _clearPeerGraceTimer() {
        if (this._peerGraceTimer) {
            clearTimeout(this._peerGraceTimer);
            this._peerGraceTimer = null;
        }
    }

    _onRoomDisconnected() {
        if (this.status === CallStatus.ACTIVE || this.status === CallStatus.CONNECTING) {
            this._finish('remote-ended', { toast: 'ended' });
        }
    }

    // --- caller signals ---------------------------------------------------

    remoteAccepted(callId, version) {
        if (this.role !== 'caller' || this.callId !== String(callId)) {
            return;
        }
        if (this._isStale(callId, version)) {
            return;
        }
        // The callee is now present. Media may still be settling: start the
        // watchdog and let peer-connected promote us to active.
        if (this.status === CallStatus.RINGING_OUT) {
            this.status = CallStatus.CONNECTING;
            this._startConnectWatchdog();
            this._emitChange();
        }
    }

    // Terminal handlers record the event's version (via _isStale) BEFORE the
    // active-call check, even when the call is unknown locally. A terminal
    // event observed for an unknown call must poison later, older events for
    // it: a CALL_CANCEL that overtakes its delayed CALL_RING (a block torn the
    // call down while the create was still publishing) would otherwise be
    // discarded, and the stale ring would then ring a user who just blocked
    // the caller.

    remoteDeclined(callId, version) {
        if (this._isStale(callId, version) || this.callId !== String(callId)) {
            return;
        }
        this._finish('declined', { toast: 'declined' });
    }

    remoteCanceled(callId, version, reason) {
        if (this._isStale(callId, version) || this.callId !== String(callId)) {
            return;
        }
        // The callee sees a caller-cancel or ring timeout as a missed call. The
        // caller only receives a cancel when the system tears its ring down (for
        // example an authorization revoked mid-ring).
        let toast = this.role === 'callee' ? 'missed' : 'canceled';
        if (this.role === 'caller' && reason === 'unanswered') {
            toast = 'no_answer';
        }
        this._finish('canceled', { toast });
    }

    answeredElsewhere(callId, version) {
        if (this._isStale(callId, version) || this.callId !== String(callId)) {
            return;
        }
        if (this.role !== 'callee' || this.status !== CallStatus.RINGING_IN) {
            return;
        }
        this._finish('answered-elsewhere', null);
    }

    remoteEnded(callId, version, duration) {
        if (this._isStale(callId, version) || this.callId !== String(callId)) {
            return;
        }
        this._finish('remote-ended', { toast: 'ended', duration });
    }

    /**
     * Re-fetch the current call after a websocket reconnect: a decline, cancel
     * or end delivered while the socket was down was lost, so without this a
     * callee keeps ringing (and a caller keeps a dead overlay) for a call the
     * backend already closed. A ring missed entirely (no local call at all)
     * cannot be recovered client-side — there is no "my active call" endpoint —
     * so this only reconciles the call this client already knows about.
     */
    async reconcileAfterReconnect() {
        if (!this.isBusy() || !this.callId) {
            return;
        }
        const expectedCallId = this.callId;

        let call;
        try {
            call = await callApi.getCall(expectedCallId);
        } catch (error) {
            // Only the API's own call-not-found (code 'call') is terminal
            // evidence. A bare 404 (a proxy or a mid-deploy route gap) has no
            // error code and must not tear down a healthy call.
            if (error instanceof CallApiError && error.status === 404 && error.code === 'call') {
                this._ifCurrent(expectedCallId, () => this._finishFromReconcile());
            }
            // Any other failure: keep the local state. Every non-idle state is
            // still bounded by the ring timer or the connect watchdog.
            return;
        }

        if (this.callId !== expectedCallId) {
            return;
        }
        const status = call && call.status;
        if (status === 'started') {
            if (this.status === CallStatus.RINGING_IN) {
                // Another device answered while this one's socket was down
                // (the CALL_ANSWERED_ELSEWHERE event was lost): this device is
                // still ringing, so the started call belongs to the accepting
                // session. Same quiet close as the live event. (This device's
                // own accept moves to CONNECTING synchronously, so it can
                // never read here as RINGING_IN + started.)
                this._finish('answered-elsewhere', null);
            } else if (this.status === CallStatus.RINGING_OUT) {
                // The callee's accept was lost with the socket. Promote
                // exactly as the live CALL_ACCEPTED handler would: media may
                // still be settling, so arm the watchdog and let
                // peer-connected finish the promotion — otherwise the 40s
                // ring timer would cancel an already-answered call.
                this._clearRingTimer();
                ringTone.stop();
                this.status = CallStatus.CONNECTING;
                this._startConnectWatchdog();
                this._emitChange();
            }
            return;
        }
        if (status === 'finished' || status === 'canceled'
            || status === 'rejected' || status === 'unanswered') {
            this._finishFromReconcile(call);
            return;
        }
        // pending, started matching an in-progress local state, or an
        // unrecognized/missing status: no evidence the call is over, so keep
        // the local state — still bounded by the ring timer and the connect
        // watchdog.
    }

    _finishFromReconcile(call) {
        const remoteStatus = call && call.status;
        // A caller whose ring outcome arrived while the socket was down gets
        // the same toast the live decline / ring-timeout path would show.
        if (this.status === CallStatus.RINGING_OUT && remoteStatus === 'rejected') {
            this._finish('reconciled', { toast: 'declined' });
            return;
        }
        if (this.status === CallStatus.RINGING_OUT && remoteStatus === 'unanswered') {
            this._finish('reconciled', { toast: 'no_answer' });
            return;
        }
        // Another of this user's own devices declined (rejected) or answered
        // and completed (finished) the ring during the outage: not a missed
        // call for this user. Quiet close, like answered-elsewhere.
        if (
            this.status === CallStatus.RINGING_IN
            && (remoteStatus === 'finished' || remoteStatus === 'rejected')
        ) {
            this._finish('reconciled', null);
            return;
        }
        // A backend 'finished' means the call was answered and completed, so
        // 'ended' reads true even when this tab never saw the active phase
        // (CONNECTING / RINGING_OUT across a full outage).
        const toast = this.status === CallStatus.RINGING_IN
            ? 'missed'
            : (this.status === CallStatus.ACTIVE || remoteStatus === 'finished' ? 'ended' : 'canceled');
        this._finish('reconciled', { toast });
    }

    // --- in-call controls -------------------------------------------------

    hangUp() {
        if (!this.callId) {
            this._finish('normal', null);
            return;
        }
        if (this.status === CallStatus.RINGING_OUT) {
            callApi.cancelCall(this.callId).catch(() => {});
        } else {
            callApi.endCall(this.callId, this._endReason()).catch(() => {});
        }
        this._finish('normal', null);
    }

    async toggleMute() {
        if (!this.client) {
            return;
        }
        this.muted = await this.client.setMuted(!this.muted);
        this._emitChange();
    }

    async toggleCamera() {
        if (!this.client || this.type !== 'video') {
            return;
        }
        this.cameraOff = !(await this.client.setCameraEnabled(this.cameraOff));
        this._emitChange();
    }

    async switchCamera() {
        if (!this.client) {
            return;
        }
        const client = this.client;
        await client.switchCamera();
        if (this.client === client) {
            this.facing = client.facingMode();
            this._emitChange();
        }
    }

    canSwitchCamera() {
        return !!(this.client && this.client.canSwitchCamera());
    }

    // --- watchdogs / timers ----------------------------------------------

    _startRingTimer(onExpire) {
        this._clearRingTimer();
        this._ringTimer = setTimeout(onExpire, RING_DURATION_MS);
    }

    _clearRingTimer() {
        if (this._ringTimer) {
            clearTimeout(this._ringTimer);
            this._ringTimer = null;
        }
    }

    _startConnectWatchdog() {
        this._clearConnectWatchdog();
        this._connectTimer = setTimeout(() => this._connectFailed(), MEDIA_CONNECT_TIMEOUT_MS);
    }

    _clearConnectWatchdog() {
        if (this._connectTimer) {
            clearTimeout(this._connectTimer);
            this._connectTimer = null;
        }
    }

    _connectFailed() {
        if (this.status === CallStatus.ACTIVE) {
            return;
        }
        if (this.callId) {
            callApi.endCall(this.callId, 'connect_failed').catch(() => {});
        }
        this._finish('connect-failed', { toast: 'connect_failed' });
    }

    // --- teardown ---------------------------------------------------------

    // The first end wins. A room disconnect or a socket end that follows a
    // local hang-up finds the call already over.
    _finish(reason, notify) {
        if (this._finishing) {
            return;
        }
        this._finishing = true;
        this.endReason = reason;
        this._teardownMedia();
        this._clearRingTimer();
        this._clearConnectWatchdog();
        this._clearPeerGraceTimer();
        ringTone.stop();

        const summary = {
            reason,
            callId: this.callId,
            // True only when this tab saw the call reach 'active'.
            connected: this.startedAt !== null,
            role: this.role,
            type: this.type,
            other: this.other,
            duration: notify && notify.duration ? Number(notify.duration) : this._elapsedSeconds(),
            toast: notify ? notify.toast : null,
        };

        this.status = CallStatus.ENDED;
        this._emitChange();
        this._emit('ended', summary);

        this._resetToIdle();
    }

    _resetToIdle() {
        this.status = CallStatus.IDLE;
        this.call = null;
        this.callId = null;
        this.role = null;
        this.type = null;
        this.other = null;
        this.livekit = null;
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.roomConnected = true;
        this.peerMissing = false;
        this.peerCameraOff = false;
        this.peerMicMuted = false;
        this.weakConnection = false;
        this.facing = 'user';
        this._mediaReady = false;
        this._finishing = false;
        this._emitChange();
    }

    _teardownMedia() {
        if (this.client) {
            try {
                this.client.disconnect();
            } catch (e) {
                /* ignore */
            }
            this.client = null;
        }
    }

    // The server records a call that never reached active as never connected.
    _endReason() {
        return this.startedAt === null ? 'connect_failed' : 'normal';
    }

    _elapsedSeconds() {
        return this.startedAt ? Math.max(0, Math.round((Date.now() - this.startedAt) / 1000)) : 0;
    }

    _errorToast(error) {
        if (error instanceof CallApiError) {
            return this._codeToast(error.code);
        }
        return 'unavailable';
    }

    _codeToast(code) {
        switch (code) {
            case 'call_invalid_state':
                return 'canceled';
            default:
                return 'unavailable';
        }
    }
}

export const callState = new CallState();

export default callState;
